import { Component, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { Command, standardCommand } from '../../../core/model/command';
import { LocalDate } from '../../../core/model/local-date';
import { Money } from '../../../core/model/money';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { ComponentMessageDispatcher } from '../../../core/service/component-message-dispather.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { leaseAgreementCommand } from '../../command/lease-agreement.command';
import { LeaseRow } from '../../model/lease-row';
import { LandlordVatLiability, MaintenanceResponsibility, PremisesContract, Responsibility } from '../../model/premises-contract';
import { Article } from '../../model/article';
import { ContractService } from '../../service/base/contract';
import { ApproveContractCommandHandler } from '../../service/command/approve-contact-command-handler.service';
import { ContractFormCommandHandlerResolver } from '../../service/command/contract-form-command-handler-resolver.service';
import { ESignKeyRecieptCommandHandler } from '../../service/command/esign-key-reciept-document-command-handler.service';
import { ESignLeaseAgreementContractCommandHandler } from '../../service/command/esign-lease-agreement-document-command-handler.service';
import { PrintContractCommandHandler } from '../../service/command/print-contract-command-handler.service';
import { PrintContractTerminationFormCommandHandler } from '../../service/command/print-contract-termination-form-command-handler';
import { ShowLeaseAgreementInvoicesCommandHandler } from '../../service/command/show-lease-agreement-invoices-command-handler';
import { TerminateContractCommandHandler } from '../../service/command/terminate-contract-command-handler.service';
import { VoidContractCommandHandler } from '../../service/command/void-contract-command-handler.service';
import { WithdrawApprovalCommandHandler } from '../../service/command/withdraw-approval-command-handler.service';
import { WithdrawTerminationCommandHandler } from '../../service/command/withdraw-termination-command-handler.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { LeaseAgreementImdConsumptionService } from '../../service/data/lease-agreement-imd-consumption.service';
import { LeaseAgreementLogService } from '../../service/data/lease-agreement-log.service';
import { PremisesContractService, PremisesDraftPropertyTaxCalculationRequest } from '../../service/data/premises-contract.service';
import { IndexValueService } from '../../service/data/index-value.service';
import { PropertyTaxCalculationService, PropertyTaxDraftCoordinator } from '../../service/data/property-tax-calculation.service';
import { ContractFormComponentBase } from '../base/contract-form.base';
import {
  PropertyTaxCalculationBasis,
  PropertyTaxCalculationBasisToString,
  PropertyTaxCalculationMethod,
  PropertyTaxCalculationMethodToString,
  LeaseAgreementStatus,
} from '../../model/const/enums';
import { Subscription, map, tap } from 'rxjs';
import { VacantRentalObject } from '../../model/vacant-rental-object';
import { LanguageService } from '../../../core/service/language.service';
import { PropertyTaxRentalChangeModel } from '../../model/property-tax-rental-change';
import {
  applyResolvedPropertyTaxMutationResult,
  buildPropertyTaxRowSignature,
  getCurrentUiPropertyTaxReplacementRow,
  getLatestActivePropertyTaxRow,
  getLatestPropertyTaxRow,
  getPropertyTaxAssessmentUnitTypeString,
  isPersistedLeaseRow,
  propertyTaxRowSignaturesMatch,
  PropertyTaxRowSignature,
  PropertyTaxRowMutationMode,
  resolvePropertyTaxRowMutation,
  runWithPropertyTaxUserTriggersSuspended,
  shouldHandlePropertyTaxUserChange,
  updatePropertyTaxRowInline,
  toPropertyTaxPercentageDisplayValue,
} from '../../helper/property-tax.helpers';
import {
  getPersistedPropertyTaxRowsSnapshot,
  havePersistedPropertyTaxRowsChanged,
  PropertyTaxChangeOrigin,
  PropertyTaxDraftResult,
  PropertyTaxFormState,
  shouldIgnoreNonDraftPropertyTaxNoOp,
} from '../../helper/property-tax-form-state';

type PropertyTaxDirtyIntent = {
  rentalObjectId: string | null;
  propertyTaxPayer: number | null;
  propertyTaxCalculationBasis: number | null;
  propertyTaxPercentage: number | null;
  propertyTaxCalculationMethod: number | null;
  propertyTaxArticleId: string | null;
  propertyTaxRetroactiveArticleId: string | null;
};

type PropertyTaxLeaseRowSnapshot = {
  article: Article | null | undefined;
  amount: Money | null | undefined;
  yearRent: Money | null | undefined;
  yearRentInclVat: Money | null | undefined;
  yearRentInCurrentYear: Money | null | undefined;
  periodRent: Money | null | undefined;
  periodRentInclVat: Money | null | undefined;
  fromDate: LocalDate | null | undefined;
  toDate: LocalDate | null | undefined;
  invoiceText: string | null | undefined;
  currencyCode: string | null | undefined;
  rentalObjectSize: number | null | undefined;
  leaseAgreementInvoicePeriod: number | null | undefined;
  rentThisPeriod: number | null | undefined;
  rentThisPeriodInclVat: number | null | undefined;
};

@Component({
    selector: 'app-premises-contract-form',
    templateUrl: './premises-contract-form.component.html',
    styleUrls: ['./premises-contract-form.component.scss'],
    providers: [
        ApproveContractCommandHandler,
        ContractFormCommandHandlerResolver,
        DeleteFormCommandHandler,
        IndexValueService,
        PropertyTaxCalculationService,
        PrintContractTerminationFormCommandHandler,
        ShowLeaseAgreementInvoicesCommandHandler,
        TerminateContractCommandHandler,
        VoidContractCommandHandler,
        ESignLeaseAgreementContractCommandHandler,
        ESignKeyRecieptCommandHandler,
        WithdrawApprovalCommandHandler,
        WithdrawTerminationCommandHandler,
        PrintContractCommandHandler,
        { provide: ContractService, useExisting: PremisesContractService },
        { provide: DataService, useExisting: PremisesContractService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PremisesContractFormComponent extends ContractFormComponentBase<PremisesContract> {
  leaseAgreementDocumentCount = 0;
  private readonly _propertyTaxFormState = new PropertyTaxFormState();
  constructor(
    activatedRoute: ActivatedRoute,
    private readonly _premisesContractService: PremisesContractService,
    dispatcher: CommandDispatcher,
    cdf: ColumnDefinitionFactory,
    cds: ColumnDefinitionService,
    fb: FormBuilder,
    fcf: FormControlExFactory,
    handlerResolver: ContractFormCommandHandlerResolver,
    messageService: MessageService,
    messageDispatcher: ComponentMessageDispatcher,
    navigation: NavigationService,
    userAccount: UserAccountService,
    logService: LeaseAgreementLogService,
    leaseAgreementImdConsumptionService: LeaseAgreementImdConsumptionService,
    private readonly _propertyTaxCalculationService: PropertyTaxCalculationService,
    private readonly _cdr: ChangeDetectorRef,
    private _languageService: LanguageService
  ) {
    super(
      messageDispatcher,
      cdf,
      fb,
      fcf,
      userAccount,
      cds,
      activatedRoute,
      _premisesContractService,
      messageService,
      navigation,
      dispatcher,
      handlerResolver,
      logService,
      leaseAgreementImdConsumptionService,
      _languageService
    );

    this.AddSubscription(
      userAccount.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Messages, Features.ManageESignatures)) {
            this.addMenuItemDefinition('COMMON.COMMAND.ESIGN_KEY_RECIEPT', leaseAgreementCommand.esignKeyReciept);
          }
          this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.PRINT', leaseAgreementCommand.print);
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.VOID', leaseAgreementCommand.void);
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          }
        },
      })
    );
    this.initCalcBasisSubscription();
    this.AddSubscription(
      this.dataContextInitialized$.subscribe(() => {
        runWithPropertyTaxUserTriggersSuspended(
          value => (this._suspendPropertyTaxUserTriggers = value),
          () => {
            this._loadedPropertyTaxRentalChange = this.dataContext?.propertyTaxRentalChange;
            this.resetPropertyTaxState();
            this.populateThePropertyTaxArticles(this.dataContext?.propertyTaxArticles);
            this.initBaselinePropertyTaxRow();
            this.refreshPropertyTaxDirtyBaseline();
            if (this.isPropertyTaxDraftEditingMode()) {
              this.scheduleDraftPropertyTaxRecalculation('hydration');
            }
          }
        );
      })
    );
  }

  onDocumentsCountChanged(count: number) {
    this.leaseAgreementDocumentCount = count;
    this._cdr.markForCheck();
  }

  responsibilitySelection: SelectionItem[] = [
    new SelectionItem('COMMON.LANDLORD', Responsibility.Landlord),
    new SelectionItem('COMMON.TENANT', Responsibility.Tenant),
  ];

  maintenanceSelection: SelectionItem[] = [
    new SelectionItem('COMMON.LANDLORD', MaintenanceResponsibility.Landlord),
    new SelectionItem('COMMON.TENANT', MaintenanceResponsibility.Tenant),
    new SelectionItem('LEASE_AGREEMENT.PREMISES_LEASE.BORDER_DRAWING_LIST', MaintenanceResponsibility.BorderDrawingList),
  ];

  yesNoSelection: SelectionItem[] = [new SelectionItem('NO_TEXT', false), new SelectionItem('YES_TEXT', true)];

  landlordVatSelection: SelectionItem[] = [
    new SelectionItem('LEASE_AGREEMENT.PREMISES_LEASE.LANDLORD_VAT_LIABLE_TYPE.NOT_LIABLE', LandlordVatLiability.NotLiable),
    new SelectionItem('LEASE_AGREEMENT.PREMISES_LEASE.LANDLORD_VAT_LIABLE_TYPE.LIABLE', LandlordVatLiability.Liable),
    new SelectionItem('LEASE_AGREEMENT.PREMISES_LEASE.LANDLORD_VAT_LIABLE_TYPE.OPTIONAL', LandlordVatLiability.Optional),
  ];

  calculationBasisOptions = [
    { name: PropertyTaxCalculationBasisToString(PropertyTaxCalculationBasis.Area), value: PropertyTaxCalculationBasis.Area },
    { name: PropertyTaxCalculationBasisToString(PropertyTaxCalculationBasis.Rent), value: PropertyTaxCalculationBasis.Rent },
    { name: PropertyTaxCalculationBasisToString(PropertyTaxCalculationBasis.Percentage), value: PropertyTaxCalculationBasis.Percentage },
  ];

  propertyTaxArticlesNonRetroactive: SelectionItem[] = [];
  propertyTaxArticlesRetroactive: SelectionItem[] = [];

  private _propertyTaxArticleChangeSub?: Subscription;
  private _propertyTaxArticlesSource: Article[] = [];
  private _loadedPropertyTaxRentalChange?: PropertyTaxRentalChangeModel;
  private _latestRentalObject?: VacantRentalObject;
  private _nonRetroPropertyTaxRowRef?: LeaseRow;
  private _nonRetroPropertyTaxRowCreatedByUi = false;
  private _baselinePropertyTaxRowRef?: LeaseRow;
  private _baselinePropertyTaxArticleId?: string;
  private _baselinePropertyTaxCalculationBasis?: PropertyTaxCalculationBasis | null;
  private _baselinePropertyTaxPercentage?: number | null;
  private _baselinePropertyTaxCalculationMethod?: PropertyTaxCalculationMethod | null;
  private _baselinePropertyTaxRowOriginalToDate?: LocalDate | null;
  private _baselinePropertyTaxRowSnapshot?: PropertyTaxLeaseRowSnapshot;
  private _baselinePropertyTaxRowSnapshotKey?: string;
  private _baselinePropertyTaxRowClosedByUi = false;
  private _uiPropertyTaxRowRef?: LeaseRow;
  private _uiPropertyTaxRowCreatedByUi = false;
  private _isHandlingNonRetroArticleChange = false;
  private _isEmittingPropertyTaxRowsChange = false;
  private _propertyTaxUserSelectionChangeHandle?: ReturnType<typeof setTimeout>;
  private _draftPropertyTaxRentalChange?: PropertyTaxRentalChangeModel | null;
  private _propertyTaxDraftCoordinator?: PropertyTaxDraftCoordinator;
  private _propertyTaxCurrentYearRentSub?: Subscription;
  private _suspendPropertyTaxUserTriggers = true;

  propertyTaxCalculationMethods = [
    {
      name: PropertyTaxCalculationMethodToString(PropertyTaxCalculationMethod.PerProperty),
      value: PropertyTaxCalculationMethod.PerProperty,
    },
    {
      name: PropertyTaxCalculationMethodToString(PropertyTaxCalculationMethod.PerBuilding),
      value: PropertyTaxCalculationMethod.PerBuilding,
    },
  ];

  get hasSecurity(): boolean {
    const hasDeposit =
      this.dataContext?.depositAmount?.amount > 0 ||
      (this.dataContext?.depositComment && this.dataContext.depositComment.trim().length > 0);

    const hasGuarantor = this.dataContext?.securities && this.dataContext.securities.length > 0;

    return hasDeposit || hasGuarantor;
  }

  conditionsForm: FormGroup;
  protected getFormGroup(): FormGroup {
    let form = super.getFormGroup();
    this.conditionsForm = this.fb.group({
      maintenanceResponsible: this.fcf.create(
        'LEASE_AGREEMENT.PREMISES_LEASE.MAINTENANCE_RESPONSIBLE',
        this.dataContext.agreementConditions.maintenanceResponsible,
        null,
        null,
        'LEASE_AGREEMENT.PREMISES_LEASE.HELP_TEXT.MAINTENANCE_RESPONSIBLE'
      ),
      securityResponsible: this.fcf.create(
        'LEASE_AGREEMENT.PREMISES_LEASE.SECURITY_RESPONSIBLE',
        this.dataContext.agreementConditions.securityResponsible,
        null,
        null,
        'LEASE_AGREEMENT.PREMISES_LEASE.HELP_TEXT.SECURITY_RESPONSIBLE'
      ),
      externalDamageResponsible: this.fcf.create(
        'LEASE_AGREEMENT.PREMISES_LEASE.EXTERNAL_DAMAGE_RESPONSIBLE',
        this.dataContext.agreementConditions.externalDamageResponsible,
        null,
        null,
        'LEASE_AGREEMENT.PREMISES_LEASE.HELP_TEXT.EXTERNAL_DAMAGE_RESPONSIBLE'
      ),
      propertyTaxPayer: this.fcf.create(
        'LEASE_AGREEMENT.PREMISES_LEASE.PROPERTY_TAX_RESPONSIBLE',
        this.dataContext.agreementConditions.propertyTaxPayer
      ),
      businessVatLiability: this.fcf.create(
        'LEASE_AGREEMENT.PREMISES_LEASE.BUSINESS_VAT_LIABLE',
        this.dataContext.agreementConditions.businessVatLiability,
        null,
        null,
        'LEASE_AGREEMENT.PREMISES_LEASE.HELP_TEXT.BUSINESS_VAT_LIABLE'
      ),
      landlordVatLiability: this.fcf.create(
        'LEASE_AGREEMENT.PREMISES_LEASE.LANDLORD_VAT_LIABLE',
        this.dataContext.agreementConditions.landlordVatLiability,
        null,
        null,
        'LEASE_AGREEMENT.PREMISES_LEASE.HELP_TEXT.LANDLORD_VAT_LIABLE'
      ),
      propertyTaxCalculationBasis: this.fcf.create(
        'PROPERTY_TAX.CALCULATION_BASIS',
        this.dataContext.agreementConditions?.propertyTaxCalculationBasis
      ),
      propertyTaxPercentage: this.fcf.create(
        'PROPERTY_TAX.PERCENTAGE_TEXT',
        toPropertyTaxPercentageDisplayValue(this.dataContext.agreementConditions?.propertyTaxPercentage)
      ),
      propertyTaxCalculationMethod: this.fcf.create(
        'PROPERTY_TAX.CALCULATION_METHOD',
        this.dataContext.agreementConditions?.propertyTaxCalculationMethod
      ),
      propertyTaxAssessmentUnitType: this.fcf.createReadonly(
        'PROPERTY_TAX.CALCULATION_BASIS_BY_TAXTYPECODE',
        this.propertyTaxAssessmentUnitTypeString
      ),
      propertyTaxArticlesNonRetroactive: this.fcf.create(
        'PROPERTY_TAX.NON_RETROACTIVE_ARTICLE',
        this.dataContext.agreementConditions?.propertyTaxArticleId
      ),
      propertyTaxArticlesRetroactive: this.fcf.create(
        'PROPERTY_TAX.RETROACTIVE_ARTICLE',
        this.dataContext.agreementConditions?.propertyTaxRetroactiveArticleId
      ),
    });
    form.addControl('agreementConditions', this.conditionsForm);
    const assessmentUnitTypeCtrl = this.conditionsForm.get('propertyTaxAssessmentUnitType');
    assessmentUnitTypeCtrl?.disable({ emitEvent: false });
    this.isPercentage = this.dataContext.agreementConditions?.propertyTaxCalculationBasis === PropertyTaxCalculationBasis.Percentage;
    this.populateThePropertyTaxArticles(this.dataContext?.propertyTaxArticles);
    this.initializePropertyTaxDraftCoordinator();
    this.bindPropertyTaxArticleChange();
    return form;
  }

  isPercentage: boolean = false;
  readonly propertyTaxSelectionTrackBy = (item: SelectionItem): unknown => item?.value ?? item?.name;
  readonly propertyTaxLeaseRowTrackBy = (row: LeaseRow): unknown => row?.id ?? this.getLeaseRowClientTrackKey(row);
  private readonly _leaseRowClientTrackKeys = new WeakMap<LeaseRow, string>();
  private _leaseRowClientTrackKeySequence = 0;

  get canEditPropertyTaxFields(): boolean {
    return this.conditionsForm?.get('propertyTaxPayer')?.value !== Responsibility.Landlord;
  }

  private getLeaseRowClientTrackKey(row: LeaseRow): string | null {
    if (!row) return null;

    let key = this._leaseRowClientTrackKeys.get(row);
    if (!key) {
      key = `premises-contract-form-lease-row-${++this._leaseRowClientTrackKeySequence}`;
      this._leaseRowClientTrackKeys.set(row, key);
    }
    return key;
  }

  private updatePropertyTaxValidators(): void {
    const propertyTaxPayer = this.conditionsForm?.get('propertyTaxPayer')?.value;
    const calculationBasis = this.conditionsForm?.get('propertyTaxCalculationBasis')?.value;
    const requiresPropertyTaxFields = propertyTaxPayer === Responsibility.Tenant;
    const requiresPercentage = requiresPropertyTaxFields && calculationBasis === PropertyTaxCalculationBasis.Percentage;

    ['propertyTaxCalculationBasis', 'propertyTaxCalculationMethod', 'propertyTaxArticlesNonRetroactive'].forEach(controlName => {
      this.updatePropertyTaxRequiredValidator(controlName, requiresPropertyTaxFields);
    });

    this.updatePropertyTaxRequiredValidator('propertyTaxPercentage', requiresPercentage);

    this.conditionsForm?.updateValueAndValidity({ emitEvent: false });
  }

  private updatePropertyTaxRequiredValidator(controlName: string, required: boolean): void {
    const control = this.conditionsForm?.get(controlName) as any;
    if (!control) return;

    if (!!control.required !== required) {
      control.required = required;
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  protected createModel(): PremisesContract {
    let model = new PremisesContract();
    model.invoiceAddress.country = '';
    return model;
  }

  private initCalcBasisSubscription(): void {
    const calcBasisCtrl = this.conditionsForm?.get('propertyTaxCalculationBasis');
    const calcMethodCtrl = this.conditionsForm?.get('propertyTaxCalculationMethod');
    const percentageCtrl = this.conditionsForm?.get('propertyTaxPercentage');
    const payerCtrl = this.conditionsForm?.get('propertyTaxPayer');
    const retroactiveArticleCtrl = this.conditionsForm?.get('propertyTaxArticlesRetroactive');
    if (!calcBasisCtrl || !calcMethodCtrl || !percentageCtrl || !payerCtrl || !retroactiveArticleCtrl) {
      setTimeout(() => this.initCalcBasisSubscription(), 0);
      return;
    }
    this.isPercentage = calcBasisCtrl.value === PropertyTaxCalculationBasis.Percentage;
    this.AddSubscription(
      calcBasisCtrl.valueChanges
        .pipe(
          tap(v => {
            this.isPercentage = v === PropertyTaxCalculationBasis.Percentage;
            this.updatePropertyTaxValidators();
            this.schedulePropertyTaxUserSelectionChange();
          })
        )
        .subscribe()
    );
    this.AddSubscription(
      calcMethodCtrl.valueChanges
        .pipe(
          tap(value => {
            this.schedulePropertyTaxUserSelectionChange();
          })
        )
        .subscribe()
    );
    this.AddSubscription(
      percentageCtrl.valueChanges
        .pipe(
          tap(() => {
            this.schedulePropertyTaxUserSelectionChange();
          })
        )
        .subscribe()
    );
    this.AddSubscription(
      payerCtrl.valueChanges
        .pipe(
          tap(() => {
            this.updatePropertyTaxValidators();
            this.handlePropertyTaxPayerChange('user');
            this.scheduleDraftPropertyTaxRecalculation('user');
          })
        )
        .subscribe()
    );
    this.AddSubscription(
      retroactiveArticleCtrl.valueChanges
        .pipe(
          tap(() => {
            this.scheduleDraftPropertyTaxRecalculation('user');
          })
        )
        .subscribe()
    );
    this.updatePropertyTaxValidators();
  }

  get propertyTaxAssessmentUnitTypeString(): string {
    return getPropertyTaxAssessmentUnitTypeString(this.dataContext?.propertyTaxAssessmentUnitType, this._languageService);
  }

  get propertyTaxRentalChangeForLeaseRows(): PropertyTaxRentalChangeModel | undefined {
    if (this._draftPropertyTaxRentalChange !== undefined) {
      return this._draftPropertyTaxRentalChange ?? undefined;
    }

    return (
      this._loadedPropertyTaxRentalChange ??
      this._latestRentalObject?.leaseAgreementTemplate?.propertyTaxRentalChange ??
      (this.dataContext as any)?.leaseAgreementTemplate?.propertyTaxRentalChange
    );
  }

  protected override prepareSave(): PremisesContract {
    const model = super.prepareSave();
    model.agreementConditions.propertyTaxPayer = this.conditionsForm.get('propertyTaxPayer')?.value;
    if (this.canEditPropertyTaxFields) {
      model.agreementConditions.propertyTaxCalculationBasis = this.conditionsForm.get('propertyTaxCalculationBasis')?.value;
      model.agreementConditions.propertyTaxPercentage = this.isPercentage
        ? this.conditionsForm.get('propertyTaxPercentage')?.value / 100
        : null;
      model.agreementConditions.propertyTaxCalculationMethod = this.conditionsForm.get('propertyTaxCalculationMethod')?.value;
      model.agreementConditions.propertyTaxArticleId = this.conditionsForm.get('propertyTaxArticlesNonRetroactive')?.value;
      model.agreementConditions.propertyTaxRetroactiveArticleId = this.conditionsForm.get('propertyTaxArticlesRetroactive')?.value;
    } else {
      model.agreementConditions.propertyTaxCalculationBasis = null;
      model.agreementConditions.propertyTaxPercentage = null;
      model.agreementConditions.propertyTaxCalculationMethod = null;
      model.agreementConditions.propertyTaxArticleId = null;
      model.agreementConditions.propertyTaxRetroactiveArticleId = null;
    }
    return model;
  }

  linkDocument() {
    const cmd = new Command<PremisesContract>(leaseAgreementCommand.linkDocument);
    cmd.context = this.dataContext;
    this.commandDispatcher.sendCommand(cmd);
  }

  override onRentalObjectChanged(rentalObject: VacantRentalObject) {
    const changeOrigin = this.resolveRentalObjectChangeOrigin(rentalObject);
    this._latestRentalObject = rentalObject;
    super.onRentalObjectChanged(rentalObject);

    runWithPropertyTaxUserTriggersSuspended(
      value => (this._suspendPropertyTaxUserTriggers = value),
      () => {
        if (rentalObject?.rentalObjectId) {
          this.dataContext.rentalObjectId = rentalObject.rentalObjectId;
        }

        const template = rentalObject?.leaseAgreementTemplate;
        const selectedNonRetroId =
          template?.propertyTaxArticleId ?? this.conditionsForm?.get('propertyTaxArticlesNonRetroactive')?.value ?? null;
        const selectedRetroId =
          template?.propertyTaxRetroArticleId ?? this.conditionsForm?.get('propertyTaxArticlesRetroactive')?.value ?? null;

        this.populateThePropertyTaxArticles(
          template?.propertyTaxArticles ?? this.dataContext?.propertyTaxArticles,
          selectedNonRetroId,
          selectedRetroId
        );
        this.initializePropertyTaxDraftCoordinator();
        this.bindPropertyTaxArticleChange();
        this.resetPropertyTaxState();
        this.initBaselinePropertyTaxRow();
        this.scheduleDraftPropertyTaxRecalculation(changeOrigin);
        if (changeOrigin === 'hydration') {
          this.refreshPropertyTaxDirtyBaseline();
        }
      }
    );

    this.handleCurrentPropertyTaxSelectionChange(changeOrigin);
  }

  protected override formSubmittedChange(response?: any) {
    super.formSubmittedChange(response);
    this.refreshPropertyTaxDirtyBaseline();
  }

  protected override formSubmittedNew(id: string) {
    super.formSubmittedNew(id);
    this.refreshPropertyTaxDirtyBaseline();
  }

  private getActiveNonRetroPropertyTaxRow(): LeaseRow | undefined {
    this.syncPropertyTaxRowReferences();

    if (this._nonRetroPropertyTaxRowRef && this.leaseAgreementRows?.includes(this._nonRetroPropertyTaxRowRef)) {
      return this._nonRetroPropertyTaxRowRef;
    }

    const active =
      getLatestActivePropertyTaxRow(this.leaseAgreementRows ?? []) ??
      (this.shouldEditPropertyTaxRowInPlace() ? getLatestPropertyTaxRow(this.leaseAgreementRows ?? []) : undefined);

    this._nonRetroPropertyTaxRowRef = active;
    this._nonRetroPropertyTaxRowCreatedByUi = !!active && !isPersistedLeaseRow(active);
    return active;
  }

  private getExistingBaselinePropertyTaxRow(): LeaseRow | undefined {
    if (this._baselinePropertyTaxRowRef && this.leaseAgreementRows?.includes(this._baselinePropertyTaxRowRef)) {
      return this._baselinePropertyTaxRowRef;
    }

    return undefined;
  }

  private clearStaleBaselinePropertyTaxState(): void {
    if (this.getExistingBaselinePropertyTaxRow()) return;

    this._baselinePropertyTaxRowRef = undefined;
    this._baselinePropertyTaxArticleId = undefined;
    this._baselinePropertyTaxCalculationBasis = undefined;
    this._baselinePropertyTaxPercentage = undefined;
    this._baselinePropertyTaxCalculationMethod = undefined;
    this._baselinePropertyTaxRowOriginalToDate = undefined;
    this._baselinePropertyTaxRowSnapshot = undefined;
    this._baselinePropertyTaxRowSnapshotKey = undefined;
    this._baselinePropertyTaxRowClosedByUi = false;
  }

  private syncPropertyTaxRowReferences(): void {
    const rows = this.leaseAgreementRows ?? [];

    if (this._uiPropertyTaxRowRef && !rows.includes(this._uiPropertyTaxRowRef)) {
      this._uiPropertyTaxRowRef = undefined;
      this._uiPropertyTaxRowCreatedByUi = false;
    }

    if (this._nonRetroPropertyTaxRowRef && !rows.includes(this._nonRetroPropertyTaxRowRef)) {
      this._nonRetroPropertyTaxRowRef = undefined;
      this._nonRetroPropertyTaxRowCreatedByUi = false;
    }

    this.clearStaleBaselinePropertyTaxState();
  }

  private bindPropertyTaxArticleChange(): void {
    this._propertyTaxArticleChangeSub?.unsubscribe();
    const control = this.conditionsForm?.get('propertyTaxArticlesNonRetroactive');
    if (!control) return;

    this._propertyTaxArticleChangeSub = control.valueChanges.subscribe(articleId => {
      this.schedulePropertyTaxUserSelectionChange();
    });
    this.AddSubscription(this._propertyTaxArticleChangeSub);
  }

  private resetPropertyTaxState(): void {
    this._propertyTaxFormState.setPendingDraftChangeOrigin('hydration');
    this._draftPropertyTaxRentalChange = undefined;
    this._nonRetroPropertyTaxRowRef = undefined;
    this._nonRetroPropertyTaxRowCreatedByUi = false;
    this._baselinePropertyTaxRowRef = undefined;
    this._baselinePropertyTaxArticleId = undefined;
    this._baselinePropertyTaxCalculationBasis = undefined;
    this._baselinePropertyTaxPercentage = undefined;
    this._baselinePropertyTaxCalculationMethod = undefined;
    this._baselinePropertyTaxRowOriginalToDate = undefined;
    this._baselinePropertyTaxRowSnapshot = undefined;
    this._baselinePropertyTaxRowSnapshotKey = undefined;
    this._baselinePropertyTaxRowClosedByUi = false;
    this._uiPropertyTaxRowRef = undefined;
    this._uiPropertyTaxRowCreatedByUi = false;
  }

  private resetPropertyTaxUiState(): void {
    this._uiPropertyTaxRowRef = undefined;
    this._uiPropertyTaxRowCreatedByUi = false;
    this._nonRetroPropertyTaxRowRef = undefined;
    this._nonRetroPropertyTaxRowCreatedByUi = false;
  }

  private setNonRetroRow(row: LeaseRow, createdByUi: boolean): void {
    this._nonRetroPropertyTaxRowRef = row;
    this._nonRetroPropertyTaxRowCreatedByUi = createdByUi;
  }

  private getPropertyTaxContext(): { currencyCode?: string; rentalObjectSize?: number; invoicePeriod?: number } {
    return {
      currencyCode: this.dataContext?.currencyCode,
      rentalObjectSize: this.rentalObjectSurface,
      invoicePeriod: this.dataContext?.invoicePeriod,
    };
  }

  private schedulePropertyTaxUserSelectionChange(): void {
    if (!shouldHandlePropertyTaxUserChange(this._suspendPropertyTaxUserTriggers)) return;

    if (this._propertyTaxUserSelectionChangeHandle) {
      clearTimeout(this._propertyTaxUserSelectionChangeHandle);
    }

    this._propertyTaxUserSelectionChangeHandle = setTimeout(() => {
      this._propertyTaxUserSelectionChangeHandle = undefined;
      this.handleCurrentPropertyTaxSelectionChange('user');
      this.scheduleDraftPropertyTaxRecalculation('user');
    }, 0);
  }

  private handleCurrentPropertyTaxSelectionChange(changeOrigin: PropertyTaxChangeOrigin = 'user'): void {
    if (changeOrigin === 'user' && !shouldHandlePropertyTaxUserChange(this._suspendPropertyTaxUserTriggers)) return;
    if (changeOrigin === 'user' && this._isEmittingPropertyTaxRowsChange) return;

    const articleId = this.conditionsForm?.get('propertyTaxArticlesNonRetroactive')?.value ?? null;
    const currentSignature = this.getCurrentPropertyTaxBaselineSignature(articleId);
    if (this.shouldIgnorePropertyTaxRowMutation(changeOrigin, currentSignature)) return;

    this.handlePropertyTaxNonRetroArticleChange(articleId, currentSignature, changeOrigin);
  }

  private handlePropertyTaxPayerChange(changeOrigin: PropertyTaxChangeOrigin = 'user'): void {
    if (changeOrigin === 'user' && !shouldHandlePropertyTaxUserChange(this._suspendPropertyTaxUserTriggers)) return;

    if (this.conditionsForm?.get('propertyTaxPayer')?.value === Responsibility.Landlord) {
      this.closeCurrentPropertyTaxRow(changeOrigin);
      return;
    }

    this.handleCurrentPropertyTaxSelectionChange(changeOrigin);
  }

  private closeCurrentPropertyTaxRow(changeOrigin: PropertyTaxChangeOrigin): void {
    if (changeOrigin === 'hydration') return;

    this.handlePropertyTaxNonRetroArticleChange(
      this.conditionsForm?.get('propertyTaxArticlesNonRetroactive')?.value ?? null,
      this.getCurrentPropertyTaxBaselineSignature(),
      changeOrigin,
      'closeOnly'
    );
  }

  private scheduleDraftPropertyTaxRecalculation(changeOrigin: PropertyTaxChangeOrigin = 'user'): void {
    if (changeOrigin === 'user' && !shouldHandlePropertyTaxUserChange(this._suspendPropertyTaxUserTriggers)) return;
    if (this.shouldIgnorePropertyTaxDraftCalculation(changeOrigin)) return;

    this._propertyTaxFormState.setPendingDraftChangeOrigin(changeOrigin);
    this._propertyTaxDraftCoordinator?.schedule();
  }

  private shouldIgnorePropertyTaxRowMutation(changeOrigin: PropertyTaxChangeOrigin, currentSignature: PropertyTaxRowSignature): boolean {
    if (this.restoreBaselinePropertyTaxRowIfNeeded(changeOrigin, currentSignature)) return true;

    return this.shouldIgnoreCurrentNonDraftPropertyTaxNoOp(changeOrigin, currentSignature);
  }

  private shouldIgnorePropertyTaxDraftCalculation(changeOrigin: PropertyTaxChangeOrigin): boolean {
    return this.shouldIgnoreCurrentNonDraftPropertyTaxNoOp(changeOrigin, this.getCurrentPropertyTaxBaselineSignature());
  }

  private shouldApplyPropertyTaxCalculationResult(
    changeOrigin: PropertyTaxChangeOrigin,
    currentSignature: PropertyTaxRowSignature
  ): boolean {
    if (this.isPropertyTaxDraftEditingMode()) return true;
    if (changeOrigin !== 'user') return false;

    return !this.shouldIgnoreCurrentNonDraftPropertyTaxNoOp(changeOrigin, currentSignature);
  }

  private restoreBaselinePropertyTaxRowIfNeeded(changeOrigin: PropertyTaxChangeOrigin, currentSignature: PropertyTaxRowSignature): boolean {
    if (!this.shouldUseBaselinePropertyTaxSnapshot(changeOrigin, currentSignature)) return false;
    if (!this.hasBaselinePropertyTaxRowChangedFromSnapshot()) return false;

    this.restoreBaselinePropertyTaxRowSnapshot(changeOrigin);
    return true;
  }

  private shouldClearBaselinePropertyTaxPreview(changeOrigin: PropertyTaxChangeOrigin, currentSignature: PropertyTaxRowSignature): boolean {
    return this.shouldUseBaselinePropertyTaxSnapshot(changeOrigin, currentSignature);
  }

  private shouldUseBaselinePropertyTaxSnapshot(changeOrigin: PropertyTaxChangeOrigin, currentSignature: PropertyTaxRowSignature): boolean {
    return (
      changeOrigin === 'user' &&
      this.isPropertyTaxComingEditingMode() &&
      !!this._baselinePropertyTaxRowSnapshot &&
      propertyTaxRowSignaturesMatch(currentSignature, this.getBaselinePropertyTaxSignature())
    );
  }

  private shouldIgnoreCurrentNonDraftPropertyTaxNoOp(
    changeOrigin: PropertyTaxChangeOrigin,
    currentSignature: PropertyTaxRowSignature
  ): boolean {
    return shouldIgnoreNonDraftPropertyTaxNoOp({
      isDraftEditingMode: this.isPropertyTaxDraftEditingMode(),
      changeOrigin,
      dirtyIntentChanged: this._propertyTaxFormState.hasDirtyIntentChanged(this.getPropertyTaxDirtyIntentSnapshot()),
      currentSignatureMatchesBaseline: propertyTaxRowSignaturesMatch(currentSignature, this.getBaselinePropertyTaxSignature()),
      hasPendingUiMutation: this.hasPendingPropertyTaxUiMutation(),
    });
  }

  private hasPendingPropertyTaxUiMutation(): boolean {
    const rows = this.leaseAgreementRows ?? [];
    return (
      this._baselinePropertyTaxRowClosedByUi ||
      (!!this._uiPropertyTaxRowRef && rows.includes(this._uiPropertyTaxRowRef)) ||
      (!!this._nonRetroPropertyTaxRowRef && this._nonRetroPropertyTaxRowCreatedByUi && rows.includes(this._nonRetroPropertyTaxRowRef))
    );
  }

  private hasBaselinePropertyTaxRowChangedFromSnapshot(): boolean {
    const row = this.getExistingBaselinePropertyTaxRow();
    if (!row || !this._baselinePropertyTaxRowSnapshotKey) return false;

    return this.getPropertyTaxLeaseRowSnapshotKey(row) !== this._baselinePropertyTaxRowSnapshotKey;
  }

  private restoreBaselinePropertyTaxRowSnapshot(changeOrigin: PropertyTaxChangeOrigin): void {
    const row = this.getExistingBaselinePropertyTaxRow();
    const snapshot = this._baselinePropertyTaxRowSnapshot;
    if (!row || !snapshot) return;

    this.leaseAgreementRows = this.leaseAgreementRows ?? [];
    const rowsBeforeRestore = getPersistedPropertyTaxRowsSnapshot(this.leaseAgreementRows);
    this.applyPropertyTaxLeaseRowSnapshot(row, snapshot);
    this.clearCurrentPropertyTaxPreviewChange();
    this._baselinePropertyTaxRowClosedByUi = false;
    this.setNonRetroRow(row, false);
    this.ensureMarkDirtyAndEmit(changeOrigin, havePersistedPropertyTaxRowsChanged(rowsBeforeRestore, this.leaseAgreementRows));
  }

  private clearCurrentPropertyTaxPreviewChange(): void {
    this._draftPropertyTaxRentalChange = null;
    if (this.dataContext) {
      this.dataContext.propertyTaxRentalChange = this._loadedPropertyTaxRentalChange;
    }
  }

  private createPropertyTaxLeaseRowSnapshot(row: LeaseRow): PropertyTaxLeaseRowSnapshot {
    const snapshot: PropertyTaxLeaseRowSnapshot = {
      article: row.article,
      amount: this.cloneMoney(row.amount, true),
      yearRent: this.cloneMoney(row.yearRent, true),
      yearRentInclVat: this.cloneMoney(row.yearRentInclVat, true),
      yearRentInCurrentYear: this.cloneMoney(row.yearRentInCurrentYear, true),
      periodRent: this.cloneMoney(row.periodRent, true),
      periodRentInclVat: this.cloneMoney(row.periodRentInclVat, true),
      fromDate: this.cloneLocalDate(row.fromDate, true),
      toDate: this.cloneLocalDate(row.toDate, true),
      invoiceText: row.invoiceText,
      currencyCode: row.currencyCode,
      rentalObjectSize: row.rentalObjectSize,
      leaseAgreementInvoicePeriod: row.leaseAgreementInvoicePeriod,
      rentThisPeriod: row.rentThisPeriod,
      rentThisPeriodInclVat: row.rentThisPeriodInclVat,
    };

    return Object.freeze(snapshot) as PropertyTaxLeaseRowSnapshot;
  }

  private applyPropertyTaxLeaseRowSnapshot(row: LeaseRow, snapshot: PropertyTaxLeaseRowSnapshot): void {
    row.article = snapshot.article;
    row.amount = this.cloneMoney(snapshot.amount);
    row.yearRent = this.cloneMoney(snapshot.yearRent);
    row.yearRentInclVat = this.cloneMoney(snapshot.yearRentInclVat);
    row.yearRentInCurrentYear = this.cloneMoney(snapshot.yearRentInCurrentYear);
    row.periodRent = this.cloneMoney(snapshot.periodRent);
    row.periodRentInclVat = this.cloneMoney(snapshot.periodRentInclVat);
    this.restoreLeaseRowDate(row, 'fromDate', '_fromDate', snapshot.fromDate);
    this.restoreLeaseRowDate(row, 'toDate', '_toDate', snapshot.toDate);
    row.invoiceText = snapshot.invoiceText;
    row.currencyCode = snapshot.currencyCode;
    row.rentalObjectSize = snapshot.rentalObjectSize;
    row.leaseAgreementInvoicePeriod = snapshot.leaseAgreementInvoicePeriod;
    row.rentThisPeriod = snapshot.rentThisPeriod;
    row.rentThisPeriodInclVat = snapshot.rentThisPeriodInclVat;
  }

  private cloneMoney(money: Money | null | undefined, freeze = false): Money | null | undefined {
    if (money === null || money === undefined) return money;

    const clone = new Money(money.currencyCode, money.amount);
    return freeze ? (Object.freeze(clone) as Money) : clone;
  }

  private cloneLocalDate(date: LocalDate | null | undefined, freeze = false): LocalDate | null | undefined {
    if (date === null || date === undefined) return date;

    const clone = LocalDate.fromDate(date);
    return freeze ? (Object.freeze(clone) as LocalDate) : clone;
  }

  private restoreLeaseRowDate(
    row: LeaseRow,
    propertyName: 'fromDate' | 'toDate',
    backingFieldName: '_fromDate' | '_toDate',
    value: LocalDate | null | undefined
  ): void {
    if (value === undefined) {
      (row as any)[backingFieldName] = undefined;
      return;
    }

    (row as any)[propertyName] = value === null ? LocalDate.None : LocalDate.fromDate(value);
  }

  private getPropertyTaxLeaseRowSnapshotKey(row: LeaseRow): string {
    return JSON.stringify({
      articleId: (row?.article as any)?.id ?? null,
      amount: this.getMoneySnapshot(row?.amount),
      yearRent: this.getMoneySnapshot(row?.yearRent),
      yearRentInclVat: this.getMoneySnapshot(row?.yearRentInclVat),
      yearRentInCurrentYear: this.getMoneySnapshot(row?.yearRentInCurrentYear),
      periodRent: this.getMoneySnapshot(row?.periodRent),
      periodRentInclVat: this.getMoneySnapshot(row?.periodRentInclVat),
      fromDate: this.getLocalDateSnapshot(row?.fromDate),
      toDate: this.getLocalDateSnapshot(row?.toDate),
      invoiceText: row?.invoiceText ?? null,
      currencyCode: row?.currencyCode ?? null,
      rentalObjectSize: row?.rentalObjectSize ?? null,
      leaseAgreementInvoicePeriod: row?.leaseAgreementInvoicePeriod ?? null,
      rentThisPeriod: row?.rentThisPeriod ?? null,
      rentThisPeriodInclVat: row?.rentThisPeriodInclVat ?? null,
    });
  }

  private getMoneySnapshot(money: Money | null | undefined): { amount: number | null; currencyCode: string | null } | null {
    if (!money) return null;

    return {
      amount: money.amount ?? null,
      currencyCode: money.currencyCode ?? null,
    };
  }

  private getLocalDateSnapshot(date: LocalDate | null | undefined): string | null {
    if (!date || !date.hasValue?.()) return null;

    return date.toString();
  }

  private initializePropertyTaxDraftCoordinator(): void {
    this._propertyTaxDraftCoordinator?.dispose();
    this._propertyTaxDraftCoordinator = this._propertyTaxCalculationService.createDraftCoordinator<
      PremisesDraftPropertyTaxCalculationRequest,
      PropertyTaxDraftResult<PropertyTaxRentalChangeModel>
    >({
      createRequest: () => this.createDraftPropertyTaxRequest(),
      executeRequest: request => {
        const changeOrigin = this._propertyTaxFormState.pendingDraftChangeOrigin;
        return this._propertyTaxCalculationService.calculateDraftPropertyTax(request).pipe(map(response => ({ changeOrigin, response })));
      },
      onResult: result => {
        const response = result?.response ?? null;
        const changeOrigin = result?.changeOrigin ?? 'hydration';
        this._draftPropertyTaxRentalChange = response === null ? null : response;
        this.dataContext.propertyTaxRentalChange = response === null ? null : response;
        if (response) {
          const articleId = this.conditionsForm?.get('propertyTaxArticlesNonRetroactive')?.value ?? null;
          if (articleId) {
            this.applyDraftPropertyTaxToCurrentRow(articleId, response, changeOrigin);
          }
        }
      },
      onError: error => {
        this.messageService.showError(error);
      },
    });
  }

  private createDraftPropertyTaxRequest(): PremisesDraftPropertyTaxCalculationRequest | null {
    const rentalObjectId = this.resolveDraftRentalObjectId();
    const propertyId = this._propertyTaxCalculationService.normalizeId(this.dataContext?.propertyId);

    return this._propertyTaxCalculationService.buildDraftPropertyTaxRequest(
      {
        propertyId,
        rentalObjectId,
        leaseAgreementId: this.getDraftPropertyTaxLeaseAgreementId(),
        propertyTaxPayer: this.conditionsForm?.get('propertyTaxPayer')?.value ?? null,
        propertyTaxCalculationBasis: this.conditionsForm?.get('propertyTaxCalculationBasis')?.value ?? null,
        propertyTaxPercentageValue: this.conditionsForm?.get('propertyTaxPercentage')?.value ?? null,
        propertyTaxCalculationMethod: this.conditionsForm?.get('propertyTaxCalculationMethod')?.value ?? null,
        propertyTaxArticleId: this.conditionsForm?.get('propertyTaxArticlesNonRetroactive')?.value ?? null,
        propertyTaxRetroactiveArticleId: this.conditionsForm?.get('propertyTaxArticlesRetroactive')?.value ?? null,
      },
      Responsibility.Tenant
    );
  }

  private resolveDraftRentalObjectId(): string | null {
    return (
      this._propertyTaxCalculationService.normalizeId(this._latestRentalObject?.rentalObjectId) ??
      this._propertyTaxCalculationService.normalizeId(this._latestRentalObject?.id) ??
      this._propertyTaxCalculationService.normalizeId(this.dataContext?.rentalObjectId) ??
      this._propertyTaxCalculationService.normalizeId(this.dataContext?.rentalObjects?.[0]?.rentalObjectId) ??
      this._propertyTaxCalculationService.normalizeId((this.dataContext?.rentalObjects?.[0] as any)?.id)
    );
  }

  private getDraftPropertyTaxLeaseAgreementId(): string | null {
    return this.isPropertyTaxDraftEditingMode() ? null : this._propertyTaxCalculationService.normalizeId(this.dataContext?.id);
  }

  private resolveRentalObjectChangeOrigin(rentalObject: VacantRentalObject | null | undefined): PropertyTaxChangeOrigin {
    const nextRentalObjectId =
      this._propertyTaxCalculationService.normalizeId(rentalObject?.rentalObjectId) ??
      this._propertyTaxCalculationService.normalizeId(rentalObject?.id);
    const currentRentalObjectId =
      this._propertyTaxCalculationService.normalizeId(this.dataContext?.rentalObjectId) ??
      this._propertyTaxCalculationService.normalizeId(this.dataContext?.rentalObjects?.[0]?.rentalObjectId) ??
      this._propertyTaxCalculationService.normalizeId((this.dataContext?.rentalObjects?.[0] as any)?.id);

    return nextRentalObjectId != null && currentRentalObjectId === nextRentalObjectId ? 'hydration' : 'user';
  }

  private handlePropertyTaxNonRetroArticleChange(
    articleId: string | null,
    currentSignature: PropertyTaxRowSignature,
    changeOrigin: PropertyTaxChangeOrigin,
    mutationMode: PropertyTaxRowMutationMode = this.getPropertyTaxRowMutationMode()
  ): void {
    if (this._isHandlingNonRetroArticleChange) return;
    this._isHandlingNonRetroArticleChange = true;
    try {
      this.leaseAgreementRows = this.leaseAgreementRows ?? [];
      const rowsBeforeMutation = getPersistedPropertyTaxRowsSnapshot(this.leaseAgreementRows);
      this.syncPropertyTaxRowReferences();
      this.initBaselinePropertyTaxRow();
      const selectedArticle = articleId ? (this._propertyTaxArticlesSource ?? []).find(a => (a as any)?.id === articleId) : undefined;
      const propertyTaxChange = this.propertyTaxRentalChangeForLeaseRows;
      const today = LocalDate.today();
      const startDate = this.getPropertyTaxMutationStartDate(today);
      const isDraftEditing = this.isPropertyTaxDraftEditingMode();
      const forceStartDate = isDraftEditing && mutationMode === 'editInPlace';
      const result = resolvePropertyTaxRowMutation(this.leaseAgreementRows, {
        articleId,
        selectedArticle,
        propertyTaxRentalChange: propertyTaxChange,
        startDate,
        today,
        forceStartDate,
        mutationMode,
        context: this.getPropertyTaxContext(),
        languageService: this._languageService,
        baselineRow: this.getExistingBaselinePropertyTaxRow(),
        baselineOriginalToDate: this._baselinePropertyTaxRowOriginalToDate,
        baselineRowClosedByUi: this._baselinePropertyTaxRowClosedByUi,
        baselineSignature: this.getBaselinePropertyTaxSignature(),
        currentSignature,
        activeRow: this.getActiveNonRetroPropertyTaxRow(),
        uiReplacementRow: getCurrentUiPropertyTaxReplacementRow(this.leaseAgreementRows, {
          uiReplacementRowRef: this._uiPropertyTaxRowRef,
          nonRetroRowRef: this._nonRetroPropertyTaxRowRef,
        }),
      });
      applyResolvedPropertyTaxMutationResult(result, {
        setBaselineRowClosedByUi: value => (this._baselinePropertyTaxRowClosedByUi = value),
        setUiReplacementRow: row => (this._uiPropertyTaxRowRef = row),
        setUiReplacementRowCreatedByUi: value => (this._uiPropertyTaxRowCreatedByUi = value),
        setNonRetroRow: (row, createdByUi) => this.setNonRetroRow(row, createdByUi),
        resetPropertyTaxUiState: () => this.resetPropertyTaxUiState(),
      });
      this.ensureMarkDirtyAndEmit(changeOrigin, havePersistedPropertyTaxRowsChanged(rowsBeforeMutation, this.leaseAgreementRows));
    } finally {
      this._isHandlingNonRetroArticleChange = false;
    }
  }

  private getBaselinePropertyTaxSignature(): PropertyTaxRowSignature | null {
    if (!this._baselinePropertyTaxRowRef) {
      const agreementConditions = this.dataContext?.agreementConditions;
      if (!agreementConditions) return null;

      return buildPropertyTaxRowSignature(
        agreementConditions.propertyTaxArticleId ?? null,
        agreementConditions.propertyTaxCalculationBasis ?? null,
        agreementConditions.propertyTaxCalculationMethod ?? null,
        {
          percentage:
            agreementConditions.propertyTaxCalculationBasis === PropertyTaxCalculationBasis.Percentage
              ? (agreementConditions.propertyTaxPercentage ?? null)
              : null,
        }
      );
    }

    return buildPropertyTaxRowSignature(
      this._baselinePropertyTaxArticleId ?? null,
      this._baselinePropertyTaxCalculationBasis ?? null,
      this._baselinePropertyTaxCalculationMethod ?? null,
      {
        percentage: this._baselinePropertyTaxPercentage ?? null,
      }
    );
  }

  private resolvePropertyTaxSelection(articleId: string | null): {
    selectedArticle: Article;
    propertyTaxChange: PropertyTaxRentalChangeModel | undefined;
    today: LocalDate;
    isBaselineScenario: boolean;
    existingRow?: LeaseRow;
    rowToEditInPlace?: LeaseRow;
  } | null {
    if (!articleId) return null;
    const selectedArticle = (this._propertyTaxArticlesSource ?? []).find(a => (a as any)?.id === articleId);
    if (!selectedArticle) return null;

    const propertyTaxChange = this.propertyTaxRentalChangeForLeaseRows;
    const today = LocalDate.today();
    const baselineRow = this.getExistingBaselinePropertyTaxRow();
    const isBaselineScenario = !!baselineRow && !!this._baselinePropertyTaxArticleId;
    const existingRow = this.getActiveNonRetroPropertyTaxRow();
    const rowToEditInPlace = isBaselineScenario ? baselineRow : existingRow;

    return { selectedArticle, propertyTaxChange, today, isBaselineScenario, existingRow, rowToEditInPlace };
  }

  private applyDraftPropertyTaxToCurrentRow(
    articleId: string,
    propertyTaxChange: PropertyTaxRentalChangeModel,
    changeOrigin: PropertyTaxChangeOrigin
  ): void {
    const currentSignature = this.getCurrentPropertyTaxBaselineSignature(articleId);
    if (this.restoreBaselinePropertyTaxRowIfNeeded(changeOrigin, currentSignature)) return;
    if (this.shouldClearBaselinePropertyTaxPreview(changeOrigin, currentSignature)) {
      this.clearCurrentPropertyTaxPreviewChange();
    }
    if (!this.shouldApplyPropertyTaxCalculationResult(changeOrigin, currentSignature)) return;

    this.syncPropertyTaxRowReferences();

    const context = this.resolvePropertyTaxSelection(articleId);
    if (!context) return;

    const targetRow = context.existingRow ?? context.rowToEditInPlace;
    if (!targetRow) {
      if (this.isPropertyTaxDraftEditingMode() || changeOrigin === 'user') {
        this.handlePropertyTaxNonRetroArticleChange(articleId, this.getCurrentPropertyTaxBaselineSignature(articleId), changeOrigin);
      }
      return;
    }
    const rowsBeforeMutation = getPersistedPropertyTaxRowsSnapshot(this.leaseAgreementRows);

    this.updatePropertyTaxRowInline(
      targetRow,
      context.selectedArticle,
      propertyTaxChange,
      this.getPropertyTaxMutationStartDate(context.today),
      {
        forceHydrateCalculatedAmounts: isPersistedLeaseRow(targetRow),
        overwritePropertyTaxAmounts: true,
        forceStartDate: this.isPropertyTaxDraftEditingMode(),
      }
    );

    this.setNonRetroRow(targetRow, !isPersistedLeaseRow(targetRow));
    this.ensureMarkDirtyAndEmit(changeOrigin, havePersistedPropertyTaxRowsChanged(rowsBeforeMutation, this.leaseAgreementRows));
    this.refreshPropertyTaxRowCurrentYearAmounts(targetRow);
  }

  private refreshPropertyTaxRowCurrentYearAmounts(row: LeaseRow): void {
    this._propertyTaxCurrentYearRentSub?.unsubscribe();
    this._propertyTaxCurrentYearRentSub = this._propertyTaxCalculationService.getCurrentPeriodRentForRow(row).subscribe({
      next: res => {
        if (res?.currentYearRent != null) {
          row.yearRentInCurrentYear = res.currentYearRent;
        }
        if (res?.periodRent != null) {
          row.rentThisPeriod = res.periodRent;
        }
        this.ensureMarkDirtyAndEmit('hydration', false);
      },
    });
  }

  private ensureMarkDirtyAndEmit(changeOrigin: PropertyTaxChangeOrigin, markDirty = true): void {
    this.leaseAgreementRows = [...this.leaseAgreementRows];
    if (
      this._propertyTaxFormState.shouldMarkDirty(changeOrigin, markDirty, this.getPropertyTaxDirtyIntentSnapshot()) &&
      this.form?.pristine
    ) {
      this.form.markAsDirty();
    }
    this._isEmittingPropertyTaxRowsChange = true;
    try {
      this.onLeaseRowsChange(this.leaseAgreementRows);
    } finally {
      setTimeout(() => {
        this._isEmittingPropertyTaxRowsChange = false;
      }, 0);
    }
  }

  private refreshPropertyTaxDirtyBaseline(): void {
    this._propertyTaxFormState.refreshDirtyBaseline(this.getPropertyTaxDirtyIntentSnapshot());
  }

  private getPropertyTaxDirtyIntentSnapshot(): string {
    const intent: PropertyTaxDirtyIntent = {
      rentalObjectId: this.resolveCurrentPropertyTaxRentalObjectId(),
      propertyTaxPayer:
        this.conditionsForm?.get('propertyTaxPayer')?.value ?? this.dataContext?.agreementConditions?.propertyTaxPayer ?? null,
      propertyTaxCalculationBasis:
        this.conditionsForm?.get('propertyTaxCalculationBasis')?.value ??
        this.dataContext?.agreementConditions?.propertyTaxCalculationBasis ??
        null,
      propertyTaxPercentage: this.conditionsForm?.get('propertyTaxPercentage')?.value ?? null,
      propertyTaxCalculationMethod:
        this.conditionsForm?.get('propertyTaxCalculationMethod')?.value ??
        this.dataContext?.agreementConditions?.propertyTaxCalculationMethod ??
        null,
      propertyTaxArticleId:
        this.conditionsForm?.get('propertyTaxArticlesNonRetroactive')?.value ??
        this.dataContext?.agreementConditions?.propertyTaxArticleId ??
        null,
      propertyTaxRetroactiveArticleId:
        this.conditionsForm?.get('propertyTaxArticlesRetroactive')?.value ??
        this.dataContext?.agreementConditions?.propertyTaxRetroactiveArticleId ??
        null,
    };

    return JSON.stringify(intent);
  }

  private resolveCurrentPropertyTaxRentalObjectId(): string | null {
    return (
      this._propertyTaxCalculationService.normalizeId(this._latestRentalObject?.rentalObjectId) ??
      this._propertyTaxCalculationService.normalizeId(this._latestRentalObject?.id) ??
      this._propertyTaxCalculationService.normalizeId(this.dataContext?.rentalObjectId) ??
      this._propertyTaxCalculationService.normalizeId(this.dataContext?.rentalObjects?.[0]?.rentalObjectId) ??
      this._propertyTaxCalculationService.normalizeId((this.dataContext?.rentalObjects?.[0] as any)?.id)
    );
  }

  private populateThePropertyTaxArticles(
    propertyTaxArticles: Article[],
    selectedNonRetroId?: string | null,
    selectedRetroId?: string | null
  ): void {
    const articles = (propertyTaxArticles ?? []).map(a => (a instanceof Article ? a : new Article(a)));
    this._propertyTaxArticlesSource = articles;

    this.propertyTaxArticlesNonRetroactive = articles
      .filter(x => !x.isRetroactiveForPropertyTax)
      .map(x => new SelectionItem(x.toString(), x.id));

    this.propertyTaxArticlesRetroactive = articles
      .filter(x => x.isRetroactiveForPropertyTax)
      .map(x => new SelectionItem(x.toString(), x.id));

    // Keep form controls in sync without emitting change events.
    const currentNonRetro = this.conditionsForm?.get('propertyTaxArticlesNonRetroactive')?.value ?? null;
    const currentRetro = this.conditionsForm?.get('propertyTaxArticlesRetroactive')?.value ?? null;

    const nonRetroId =
      selectedNonRetroId !== undefined
        ? selectedNonRetroId
        : (this.dataContext?.agreementConditions?.propertyTaxArticleId ?? currentNonRetro);
    const retroId =
      selectedRetroId !== undefined
        ? selectedRetroId
        : (this.dataContext?.agreementConditions?.propertyTaxRetroactiveArticleId ?? currentRetro);

    // Only set ids that exist in the current option set to avoid invalid values.
    const hasNonRetro = this.propertyTaxArticlesNonRetroactive.some(x => x.value === nonRetroId);
    const hasRetro = this.propertyTaxArticlesRetroactive.some(x => x.value === retroId);

    this.conditionsForm?.get('propertyTaxArticlesNonRetroactive')?.setValue(hasNonRetro ? nonRetroId : null, { emitEvent: false });
    this.conditionsForm?.get('propertyTaxArticlesRetroactive')?.setValue(hasRetro ? retroId : null, { emitEvent: false });
  }

  private initBaselinePropertyTaxRow(): void {
    if (this.isPropertyTaxDraftEditingMode()) return;
    if (this._baselinePropertyTaxRowRef) return;

    const propertyTaxRows = (this.leaseAgreementRows ?? []).filter(row => row?.article?.isPropertyTax && isPersistedLeaseRow(row));
    const active = this.isPropertyTaxComingEditingMode()
      ? getLatestPropertyTaxRow(propertyTaxRows)
      : getLatestActivePropertyTaxRow(propertyTaxRows, { onlyPersisted: true });

    if (!active) return;

    this._baselinePropertyTaxRowRef = active;
    this._baselinePropertyTaxRowSnapshot = this.createPropertyTaxLeaseRowSnapshot(active);
    this._baselinePropertyTaxRowSnapshotKey = this.getPropertyTaxLeaseRowSnapshotKey(active);
    this._baselinePropertyTaxArticleId = (active.article as any)?.id;
    this._baselinePropertyTaxCalculationBasis = this.dataContext?.agreementConditions?.propertyTaxCalculationBasis ?? null;
    this._baselinePropertyTaxPercentage =
      this._baselinePropertyTaxCalculationBasis === PropertyTaxCalculationBasis.Percentage
        ? (this.dataContext?.agreementConditions?.propertyTaxPercentage ?? null)
        : null;
    this._baselinePropertyTaxCalculationMethod = this.dataContext?.agreementConditions?.propertyTaxCalculationMethod ?? null;
    this._baselinePropertyTaxRowOriginalToDate = active.toDate && active.toDate.hasValue() ? LocalDate.fromDate(active.toDate) : undefined;
  }

  private getCurrentPropertyTaxBaselineSignature(selectedArticleId?: string | null): PropertyTaxRowSignature {
    return buildPropertyTaxRowSignature(
      selectedArticleId ?? this.conditionsForm?.get('propertyTaxArticlesNonRetroactive')?.value ?? null,
      this.conditionsForm?.get('propertyTaxCalculationBasis')?.value ?? null,
      this.conditionsForm?.get('propertyTaxCalculationMethod')?.value ?? null,
      {
        percentage: this.getPropertyTaxPercentageSignatureValue(),
      }
    );
  }

  private getPropertyTaxPercentageSignatureValue(): number | null {
    if (this.conditionsForm?.get('propertyTaxCalculationBasis')?.value !== PropertyTaxCalculationBasis.Percentage) return null;

    const value = this.conditionsForm?.get('propertyTaxPercentage')?.value;
    return value == null ? null : value / 100;
  }

  private getPropertyTaxMutationStartDate(fallbackDate: LocalDate): LocalDate {
    if (!this.isPropertyTaxDraftEditingMode()) {
      return fallbackDate;
    }

    const contractStartDate = LocalDate.fromDate(this.fromDate ?? this.dataContext?.fromDate);
    return contractStartDate?.hasValue() ? contractStartDate : fallbackDate;
  }

  private isPropertyTaxDraftEditingMode(): boolean {
    return this.isNew || this.dataContext?.isNew || this.dataContext?.status === LeaseAgreementStatus.Draft;
  }

  private isPropertyTaxComingEditingMode(): boolean {
    return this.dataContext?.status === LeaseAgreementStatus.Coming;
  }

  private shouldEditPropertyTaxRowInPlace(): boolean {
    return this.isPropertyTaxDraftEditingMode() || this.isPropertyTaxComingEditingMode();
  }

  private getPropertyTaxRowMutationMode(): PropertyTaxRowMutationMode {
    return this.shouldEditPropertyTaxRowInPlace() ? 'editInPlace' : 'replaceFromToday';
  }

  private updatePropertyTaxRowInline(
    row: LeaseRow,
    selectedArticle: Article,
    propertyTaxChange: PropertyTaxRentalChangeModel | undefined,
    startDate?: LocalDate,
    options?: { forceHydrateCalculatedAmounts?: boolean; overwritePropertyTaxAmounts?: boolean; forceStartDate?: boolean }
  ): void {
    updatePropertyTaxRowInline(row, selectedArticle, propertyTaxChange, this._languageService, {
      currencyCode: this.dataContext?.currencyCode,
      rentalObjectSize: this.rentalObjectSurface,
      invoicePeriod: this.dataContext?.invoicePeriod,
      startDate,
      forceHydrateCalculatedAmounts: options?.forceHydrateCalculatedAmounts,
      overwritePropertyTaxAmounts: options?.overwritePropertyTaxAmounts,
      forceStartDate: options?.forceStartDate,
    });
  }

  override ngOnDestroy(): void {
    if (this._propertyTaxUserSelectionChangeHandle) {
      clearTimeout(this._propertyTaxUserSelectionChangeHandle);
    }
    this._propertyTaxDraftCoordinator?.dispose();
    this._propertyTaxCurrentYearRentSub?.unsubscribe();

    super.ngOnDestroy();
  }
}
